import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { SkipLink } from '@gousto-internal/citrus-react'
import Helmet from 'react-helmet'
import { forceCheck as forceCheckLazyload } from 'react-lazyload'
import { useDispatch, useSelector } from 'react-redux'

import {
  ResubscriptionConfirmationModalHost,
  useStagedResubscriptionModal,
} from '@features/resub-optimisation'
import { ERROR_COMPONENT_MODES, InstrumentedErrorComponent } from '@library/browser-errors'
import { MenuUnsavedChangesPrompt } from '@library/menu-page'
import { BROWSER_ERROR_KEYS } from '@library/observability/browser'

import { hydrateOneOffOrder } from 'actions/oneOffOrder'
import redirectActions from 'actions/redirect'
import { ErrorPage } from 'components/ErrorPage'
import { RibbonTrigger } from 'components/RibbonTrigger'
import menu from 'config/menu'
import { client as clientRoutes } from 'config/routes'
import { MainLayout } from 'layouts/MainLayout'
import { MenuExperienceBasketContainer } from 'routes/Menu/MenuExperience/Basket/MenuExperienceBasketContainer'
import { useMenuExperienceRefresh } from 'routes/Menu/MenuExperience/MenuExperiencePage/hooks/useMenuExperienceRefresh'
import { MenuOverlay } from 'routes/Menu/MenuOverlay'
import type { MenuProps } from 'routes/Menu/MenuProps'
import { menuOverlayClick } from 'routes/Menu/actions/menuOverlayClick'
import { useBasket } from 'routes/Menu/domains/basket'
import { useMenuDeeplinkHydration } from 'routes/Menu/experiments/GoustoAnytime/useMenuDeeplinkHydration'
import { useMenuUnsavedChangesModal } from 'routes/Menu/hooks/useMenuUnsavedChangesModal'
import { useUnsavedChangesPrompt } from 'routes/Menu/hooks/useUnsavedChangesPrompt'
import { getBasketSaveRequired } from 'selectors/basket'

import {
  MENU_BOX_SUMMARY_ID,
  MENU_CONTAINER_ID,
  MENU_RECIPE_LIST_SKIP_TARGET_ID,
} from './accessibility'
import { BasketChangesModal } from './components/Basket/BasketChangesModal'
import { CheckoutErrorModal } from './components/Basket/CheckoutErrorModal'
import { BoxSummaryContainer } from './components/BoxSummary'
import { MenuError } from './components/MenuError'
import { MenuPreferencesSidebar } from './components/MenuPreferences/MenuPreferencesSidebar'
import { RecipesInBasketProgress } from './components/RecipesInBasketProgress'
import { RecipeSearchModal } from './components/Search/RecipeSearchModal/RecipeSearchModal'
import { MenuQueryContextProvider } from './context/menuQueryContext'
import { TutorialsBlockedProvider } from './context/tutorialsBlockedContext'

import css from './Menu.css'

// Once a one-off hydration has applied the deeplinked day/slot to the basket, drop
// those ids so fetchData's beforeMenuLoad doesn't re-run setSlotFromIds and clear
// the chosen slot (it dispatches basketSlotChange('') for a day_id with no slot_id).
const stripDeeplinkIds = <T extends { day_id?: unknown; slot_id?: unknown }>(query: T) => {
  const { day_id: _dayId, slot_id: _slotId, ...queryWithoutDeeplinkIds } = query

  return queryWithoutDeeplinkIds
}

export const Menu: React.FC<MenuProps> = (props) => {
  const dispatch = useDispatch()
  const { addRecipeByUuid, date, recipeCount } = useBasket()
  const basketSaveRequired = useSelector(getBasketSaveRequired)
  const isUnsavedChangesPromptEnabled = useUnsavedChangesPrompt()
  const stagedResubscriptionModal = useStagedResubscriptionModal()
  const [isResubscriptionModalOpen, setIsResubscriptionModalOpen] = useState(
    stagedResubscriptionModal !== null,
  )
  const closeResubscriptionModal = useCallback(() => setIsResubscriptionModalOpen(false), [])

  const {
    params,
    route,
    router,
    query,
    setBasketNumPortion,
    menuCalculateTimeToUsable,
    fetchData,
    applyPromoCode,
    hasBasketPromo,
    showOverlay,
    isAuthenticated,
    children,
    hasMenuError,
    loginVisibilityChange,
  } = props

  const refreshMenuExperience = useMenuExperienceRefresh(query)

  const { orderId } = params

  const isEditingOrder = Boolean(orderId)
  const promptVariant = isEditingOrder ? 'edit' : 'new'
  const shouldBlockNavigation = isEditingOrder
    ? basketSaveRequired
    : Boolean(date) && recipeCount > 0
  const { handleLeave, handleStay } = useMenuUnsavedChangesModal({
    isEnabled: isUnsavedChangesPromptEnabled,
    route,
    router,
    shouldBlockNavigation,
    variant: promptVariant,
  })

  const prevIsAuthenticated = useRef(isAuthenticated)
  const { isEligible, isResolved } = useMenuDeeplinkHydration()

  // Authenticated customers with no delivery date are normally bounced to
  // /delivery-details. For TRX-variant customers (TM-1571) we suppress that bounce
  // and hydrate a one-off order instead. We must wait for the experiment decision
  // to resolve before doing anything, otherwise eligible customers get redirected
  // (or the menu loads without hydration) during the async decision load.
  const isAwaitingDeeplinkDecision = isAuthenticated && !orderId && !date && !isResolved
  const shouldRedirectToDeliveryDetails =
    isAuthenticated && !orderId && !date && isResolved && !isEligible

  // Only sanitise the query once a one-off hydration has actually handled the
  // deeplink ids (set below). When hydration is skipped — e.g. the customer
  // already has a basket date — leave day_id/slot_id intact so fetchData still
  // honours the deeplinked day/slot. Shared by refreshMenu and the auth-change
  // fetch so refreshes after hydration keep the chosen slot too.
  const [deeplinkHandled, setDeeplinkHandled] = useState(false)
  const menuQuery = useMemo(
    () => (deeplinkHandled ? stripDeeplinkIds(query) : query),
    [deeplinkHandled, query],
  )

  useEffect(() => {
    if (shouldRedirectToDeliveryDetails) {
      dispatch(redirectActions.replace(clientRoutes.deliveryDetails))
    }
  }, [dispatch, shouldRedirectToDeliveryDetails])

  const hasInitiatedLoad = useRef(false)

  useEffect(() => {
    // Hold off until we know whether this customer should land on the menu directly.
    if (isAwaitingDeeplinkDecision || hasInitiatedLoad.current) {
      return
    }
    hasInitiatedLoad.current = true

    const loadMenu = async () => {
      const forceDataLoad = Boolean(query.reload)
      // TODO: Add back logic to check what needs to be reloaded

      if (query?.num_portions) {
        setBasketNumPortion(query.num_portions)
      }

      let effectiveQuery = menuQuery

      if (isEligible && !orderId && !date) {
        const { ok } = (await dispatch(
          hydrateOneOffOrder({ dayId: query.day_id, slotId: query.slot_id }) as any,
        )) as unknown as { ok: boolean }

        // Bad/conflicting deeplink ids (or no available slot) fall back to the
        // standard /delivery-details flow, exactly like a control customer.
        if (!ok) {
          dispatch(redirectActions.replace(clientRoutes.deliveryDetails))

          return
        }

        // Hydration applied the ids; sanitise this initial fetch (the state
        // update isn't reflected synchronously) and every later refresh.
        setDeeplinkHandled(true)
        effectiveQuery = stripDeeplinkIds(query)
      }

      await fetchData({ query: effectiveQuery, params }, forceDataLoad, undefined, {
        addRecipe: addRecipeByUuid,
      })

      const promises = []
      if (!orderId && !hasBasketPromo) {
        promises.push(applyPromoCode())
      }

      await Promise.all(promises)
      menuCalculateTimeToUsable()
    }

    loadMenu()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAwaitingDeeplinkDecision])

  useEffect(
    () => () => {
      loginVisibilityChange(false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    const isAdminQuery = !!(query && query['preview[auth_user_id]'])
    if (!isAdminQuery && prevIsAuthenticated.current !== isAuthenticated) {
      fetchData({ query: menuQuery, params }, false, undefined, {
        addRecipe: addRecipeByUuid,
      })
    }

    forceCheckLazyload()

    // Update refs after effect runs
    prevIsAuthenticated.current = isAuthenticated
  }, [isAuthenticated, query, menuQuery, params, addRecipeByUuid, fetchData])

  useEffect(() => {
    if (hasMenuError) {
      datadogRum.addAction('user_saw_menu_page_error')
    }
  }, [hasMenuError])

  const onOverlayClick = () => {
    dispatch(menuOverlayClick())
  }

  const retryMenuDataFetch = () =>
    fetchData({ query: menuQuery, params }, true, undefined, { addRecipe: addRecipeByUuid })

  const refreshMenuAfterPreferencesSave = () => {
    if (isAuthenticated) {
      return refreshMenuExperience()
    }

    return retryMenuDataFetch()
  }

  const onBoxSummarySkipLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault()

    const focusTarget = document.querySelector<HTMLElement>(
      [
        `#${MENU_BOX_SUMMARY_ID} [data-testid="checkout-button"]`,
        `#${MENU_BOX_SUMMARY_ID} [data-testid="viewBasketButton"] button`,
        `#${MENU_BOX_SUMMARY_ID} [data-testing="expandBoxSummaryButton"]`,
        `#${MENU_BOX_SUMMARY_ID} [data-testing="boxSummaryButton"]`,
      ].join(','),
    )

    if (focusTarget) {
      focusTarget.focus({ preventScroll: true })

      return
    }

    const boxSummaryTarget = document.getElementById(MENU_BOX_SUMMARY_ID)
    if (boxSummaryTarget instanceof HTMLElement) {
      boxSummaryTarget.focus({ preventScroll: true })
    }
  }

  if (!isAuthenticated && orderId) {
    return (
      <InstrumentedErrorComponent
        errorKey={BROWSER_ERROR_KEYS.INVALID_ORDER_ACCESS_MENU_ERROR}
        mode={ERROR_COMPONENT_MODES.ON_RENDER}
        orderId={orderId}
      >
        <MainLayout>
          <ErrorPage />
        </MainLayout>
      </InstrumentedErrorComponent>
    )
  }

  if (hasMenuError) {
    return (
      <MainLayout>
        <MenuError onRefresh={retryMenuDataFetch} />
      </MainLayout>
    )
  }

  const menuSkipLinks = (
    <>
      <SkipLink targetId={MENU_CONTAINER_ID}>Navigate to menu content</SkipLink>
      <SkipLink targetId={MENU_RECIPE_LIST_SKIP_TARGET_ID}>Navigate to recipe list</SkipLink>
      <SkipLink targetId={MENU_BOX_SUMMARY_ID} onClick={onBoxSummarySkipLinkClick}>
        Navigate to box summary
      </SkipLink>
    </>
  )

  if (shouldRedirectToDeliveryDetails) {
    return null
  }

  return (
    <MainLayout route={{ withRecipeBar: true }} preHeaderContent={menuSkipLinks}>
      <TutorialsBlockedProvider value={isResubscriptionModalOpen}>
        <MenuQueryContextProvider value={query}>
          <div id={MENU_CONTAINER_ID} data-testing="menuContainer" tabIndex={-1}>
            <Helmet title={menu.helmet.title} meta={menu.helmet.meta} style={menu.helmet.style} />
            <div className={css.container}>{children}</div>
            {
              // overlay cannot be focused with keyboard, so it makes no sense
              // to add keyboard handling for it
            }
            <MenuOverlay
              className={showOverlay ? css.greyOverlayShow : css.greyOverlay}
              onClick={onOverlayClick}
            />
            {isUnsavedChangesPromptEnabled && (
              <MenuUnsavedChangesPrompt
                onLeave={handleLeave}
                onStay={handleStay}
                variant={promptVariant}
              />
            )}
            {isAuthenticated && <CheckoutErrorModal />}
            {isAuthenticated && <BasketChangesModal />}
            <div id={MENU_BOX_SUMMARY_ID} tabIndex={-1}>
              {isAuthenticated ? <MenuExperienceBasketContainer /> : <BoxSummaryContainer />}
            </div>
            {!isAuthenticated && <RecipesInBasketProgress />}
          </div>
          <RibbonTrigger name="menu" />
        </MenuQueryContextProvider>
        <RecipeSearchModal />
        <ResubscriptionConfirmationModalHost
          staged={stagedResubscriptionModal}
          focusTargetId={MENU_CONTAINER_ID}
          onClosed={closeResubscriptionModal}
        />
        <MenuPreferencesSidebar onRefresh={refreshMenuAfterPreferencesSave} />
      </TutorialsBlockedProvider>
    </MainLayout>
  )
}
